import { getAuthToken } from '../utils/apiClient';

/* ── Custom error class — preserves HTTP status code ── */
export class CarrierApiError extends Error {
  constructor(public statusCode: number, message: string) {
    super(message);
    this.name = 'CarrierApiError';
  }
}

/* ── Shapes ── */
export interface Carrier {
  id: string;
  name: string;
  logoUrl?: string;
  isActive: boolean;
  createdAt: string;
}

export interface CarrierDownloadRequest {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  notes: string | null;
  requested_at: string;
  updated_at?: string;
  subscribed: boolean;
}

export interface CarrierDownloadRequestsResponse {
  requests: CarrierDownloadRequest[];
  total: number;
}

export interface SessionLogRecord {
  databaseId: string;
  createDate: string;
  changeDate: string;
  transactionRequestDate: string;
  type: string;
  xmlType: string;
  provider: string;
  consumed: string;
  carriersNaics: string;
  carriersNames: string;
  consumedRows: string;
  number: number | null;
  totalItems: number | null;
  directBillTotalGrossAmount: number | null;
  directBillTotalCommissionAmount: number | null;
  directBillStatementDate: string | null;
}

export interface SessionLogsResponse {
  records: SessionLogRecord[];
  total: number;
}

export interface SessionDetailItem {
  RowNumber: number;
  Transaction: string;
  TransactionEffectiveDate: string;
  TransactionRequestDate: string;
  Carrier: string;
  InsuredName: string;
  InsuredAddress: string;
  LineOfBusiness: string;
  PolicyNumber: string;
  Premium: number | null;
  NonPremium: number | null;
  EffectiveDate: string;
  ExpirationDate: string;
  Consumed: boolean;
  DownloadLogs: string[];
  Details: {
    Action: { ActionType: number; Description?: string };
    CarrierHr: { Id: string; Name: string; NavigateUrl?: string };
    InsuredHr: { Id: string; Name: string; NavigateUrl?: string; Address?: string };
    TransactionDescription: string;
    Agent: { Text?: string; Value: string };
    PolicyHr: { Id: string; Name: string; NavigateUrl?: string };
  };
}

export interface SessionDetailsResponse {
  Items: SessionDetailItem[];
}

export interface MergePreviewRecord {
  session_create_date: string | null;
  session_id: string;
  transaction_code: string;
  transaction_string: string;
  merge_status: string;
  insured_name_ivans: string;
  insured_address_ivans: string;
  policy_number_ivans: string;
  line_of_business: string;
  policy_effective_date: string;
  policy_expiration_date: string;
  carrier_name_ivans: string;
  action_category: string;
  planned_action_reason: string;
  insured_url: string | null;
  policy_url: string | null;
  carrier_url: string | null;
}

export interface MergePreviewResponse {
  records: MergePreviewRecord[];
  total: number;
}

/* ── Internal fetch helper (preserves status code) ── */
async function carrierFetch<T>(path: string, options: RequestInit = {}): Promise<{ data: T; status: number }> {
  const token = getAuthToken();

  const response = await fetch(`${process.env.REACT_APP_Auth_Url}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string> || {}),
    },
  });

  if (response.status === 401) {
    throw new CarrierApiError(401, 'Session expired. Please log in again.');
  }

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // non-JSON body — keep default message
    }
    throw new CarrierApiError(response.status, message);
  }

  const data = await response.json() as T;
  return { data, status: response.status };
}

/* ── Public API functions ── */

export async function getActiveCarriers(): Promise<Carrier[]> {
  const { data } = await carrierFetch<Carrier[] | { carriers: Carrier[] }>('/api/momentumamp/carriers');
  return Array.isArray(data) ? data : (data.carriers ?? []);
}

/**
 * Submit a new carrier download request.
 * @returns The created request record
 * @throws CarrierApiError with statusCode 409 if a request is already in progress
 */
export async function submitCarrierDownloadRequest(): Promise<CarrierDownloadRequest> {
  const { data } = await carrierFetch<CarrierDownloadRequest>('/api/carrier-download/request', {
    method: 'POST',
  });
  return data;
}

/**
 * Get the current user's carrier download request history.
 * @param limit Maximum number of requests to return (default: 20)
 * @param offset Number of requests to skip for pagination (default: 0)
 * @returns Object with requests array and total count
 */
export async function getMyCarrierDownloadRequests(
  limit: number = 20,
  offset: number = 0
): Promise<CarrierDownloadRequestsResponse> {
  const { data } = await carrierFetch<CarrierDownloadRequestsResponse>(
    `/api/carrier-download/my-requests?limit=${limit}&offset=${offset}`
  );
  return data;
}

// Both carrier-download list endpoints require from_date AND to_date (an empty value
// is a 400). The UI's "Clear" (All dates) and a one-sided range leave one or both
// blank, so fill the open end: earliest → EARLIEST_DATE, latest → today (local).
const EARLIEST_DATE = '2000-01-01';
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function appendDateRange(form: FormData, from: string, to: string): void {
  form.append('from_date', from || EARLIEST_DATE);
  form.append('to_date',   to || localToday());
}

export async function getCarrierDownloadSessionLogs(
  from: string,
  to: string,
  limit: number = 100,
  offset: number = 0
): Promise<SessionLogsResponse> {
  const token    = getAuthToken();
  const nc_token = localStorage.getItem('amsToken') || '';

  const form = new FormData();
  form.append('nc_token',  nc_token);
  appendDateRange(form, from, to);
  form.append('skip',      String(offset));
  form.append('top',       String(limit));

  const response = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/ivans-sessions-list`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (response.status === 401) {
    throw new CarrierApiError(401, 'Session expired. Please log in again.');
  }
  if (!response.ok) throw new CarrierApiError(response.status, `Request failed: ${response.status}`);

  const json = await response.json();

  const raw: any[] = json?.value ?? (Array.isArray(json) ? json : []);
  const total: number = json?.['@odata.count'] ?? raw.length;

  const records: SessionLogRecord[] = raw.map((r: any) => ({
    databaseId:                    r.databaseId                    ?? '',
    createDate:                    r.createDate                    ?? '',
    changeDate:                    r.changeDate                    ?? '',
    transactionRequestDate:        r.transactionRequestDate        ?? '',
    type:                          r.type                          ?? '',
    xmlType:                       r.xmlType                       ?? '',
    provider:                      r.provider                      ?? '',
    consumed:                      r.consumed                      ?? '',
    carriersNaics:                 r.carriersNaics                 ?? '',
    carriersNames:                 r.carriersNames                 ?? '',
    consumedRows:                  r.consumedRows                  ?? '',
    number:                        r.number                        ?? null,
    totalItems:                    r.totalItems                    ?? null,
    directBillTotalGrossAmount:    r.directBillTotalGrossAmount    ?? null,
    directBillTotalCommissionAmount: r.directBillTotalCommissionAmount ?? null,
    directBillStatementDate:       r.directBillStatementDate       ?? null,
  }));

  return { records, total };
}

export async function getCarrierDownloadSessionDetails(
  sessionId: string,
  consumedRows: string
): Promise<SessionDetailsResponse> {
  const token         = getAuthToken();
  const nc_token      = localStorage.getItem('amsToken')      || '';
  const agency_domain = localStorage.getItem('agencyDomain')  || '';

  const form = new FormData();
  form.append('nc_token',         nc_token);
  form.append('agency_domain',    agency_domain);
  form.append('ivans_session_id', sessionId);
  form.append('consumed_rows',    consumedRows);

  const response = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/ivans-session-details`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (response.status === 401) {
    throw new CarrierApiError(401, 'Session expired. Please log in again.');
  }
  if (!response.ok) throw new CarrierApiError(response.status, `Request failed: ${response.status}`);

  return response.json();
}

export async function getCarrierDownloadMergePreview(
  from: string,
  to: string,
  limit: number = 100,
  offset: number = 0
): Promise<MergePreviewResponse> {
  const token     = getAuthToken();
  const agency_id = localStorage.getItem('NCAgencyId') || '';

  const form = new FormData();
  form.append('agency_id', agency_id);
  appendDateRange(form, from, to);
  form.append('skip',      String(offset));
  form.append('top',       String(limit));

  const response = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/carrier-download-session-rows`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (response.status === 401) {
    throw new CarrierApiError(401, 'Session expired. Please log in again.');
  }
  if (!response.ok) throw new CarrierApiError(response.status, `Request failed: ${response.status}`);

  const json = await response.json();
  const records: MergePreviewRecord[] = json?.items ?? [];
  const total: number = json?.total ?? records.length;

  return { records, total };
}

/* ── Per-carrier download rules ──
 * [NEEDS VERIFICATION] Endpoint paths/payloads below are assumed to sit beside
 * /api/carrier-download/request on the auth API — confirm with the backend team.
 */
export type CarrierRuleAction = 'direct_merge' | 'ai_merge' | 'user_review' | 'review_remarks' | 'remarks_to_notes';

export interface CarrierDownloadRule {
  id: string;
  carrierId: string;
  title: string;
  action: CarrierRuleAction;
  remarks: string;
  /** Optional: only apply when the carrier's remarks contain this text (blank = always). */
  matchText: string;
  enabled: boolean;
}

export type CarrierDownloadRuleInput = Omit<CarrierDownloadRule, 'id'>;

/* Front-end-only mode (default): rules persist in this browser's localStorage.
 * Set REACT_APP_CARRIER_RULES_API=true to use the backend endpoints above instead. */
const USE_RULES_API = process.env.REACT_APP_CARRIER_RULES_API === 'true';
const LOCAL_RULES_KEY = 'carrierDownloadRules';

function readLocalRules(): CarrierDownloadRule[] {
  try { return JSON.parse(localStorage.getItem(LOCAL_RULES_KEY) || '[]'); } catch { return []; }
}
function writeLocalRules(rules: CarrierDownloadRule[]) {
  localStorage.setItem(LOCAL_RULES_KEY, JSON.stringify(rules));
}

export async function getCarrierDownloadRules(carrierId: string): Promise<CarrierDownloadRule[]> {
  if (!USE_RULES_API) return readLocalRules().filter(r => r.carrierId === carrierId);
  const { data } = await carrierFetch<CarrierDownloadRule[] | { rules: CarrierDownloadRule[] }>(
    `/api/carrier-download/rules?carrierId=${encodeURIComponent(carrierId)}`
  );
  return Array.isArray(data) ? data : (data.rules ?? []);
}

export async function createCarrierDownloadRule(rule: CarrierDownloadRuleInput): Promise<CarrierDownloadRule> {
  if (!USE_RULES_API) {
    const created: CarrierDownloadRule = { ...rule, id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
    writeLocalRules([...readLocalRules(), created]);
    return created;
  }
  const { data } = await carrierFetch<CarrierDownloadRule>('/api/carrier-download/rules', {
    method: 'POST',
    body: JSON.stringify(rule),
  });
  return data;
}

export async function updateCarrierDownloadRule(id: string, rule: CarrierDownloadRuleInput): Promise<CarrierDownloadRule> {
  if (!USE_RULES_API) {
    const updated: CarrierDownloadRule = { ...rule, id };
    writeLocalRules(readLocalRules().map(r => (r.id === id ? updated : r)));
    return updated;
  }
  const { data } = await carrierFetch<CarrierDownloadRule>(`/api/carrier-download/rules/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(rule),
  });
  return data;
}

export async function deleteCarrierDownloadRule(id: string): Promise<void> {
  if (!USE_RULES_API) { writeLocalRules(readLocalRules().filter(r => r.id !== id)); return; }
  await carrierFetch<unknown>(`/api/carrier-download/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Sample carriers used only in front-end-only mode when the carriers API can't be reached. */
export const SAMPLE_CARRIERS: Carrier[] = ['Progressive', 'Travelers', 'Liberty Mutual', 'National General', 'Mercury Insurance', 'Openly']
  .map(name => ({ id: `sample-${name.toLowerCase().replace(/\s+/g, '-')}`, name, isActive: true, createdAt: '' }));
